'use client';

import React, { useCallback, useContext, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import type { EChartsOption } from 'echarts';
import { ChartManager } from '@benzinga/chart-manager';
import { safeTimeout } from '@benzinga/safe-await';
import { SessionContext } from '@benzinga/session-context';
import { isMobile as isMobileDevice } from '@benzinga/device-utils';
import { formatTradeSize, formatDateLabel, formatShareAxisLabel } from './formatters';
import styles from '../../insiderTrades.module.scss';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const INDEX_OPTIONS = [
  { symbol: 'SPY', label: 'S&P 500 Index' },
  { symbol: 'XLY', label: 'XLY Consumer Discretionary' },
  { symbol: 'VCR', label: 'VCR Consumer Discretionary' },
  { symbol: 'VGT', label: 'VGT Technology' },
] as const;

export type IndexPricePoint = { date: string; close: number };

export type DailyActivityPoint = {
  date: string;
  buy_volume_dollars: number;
  sell_volume_dollars: number;
};

type ChartDataPoint = {
  date: string;
  dateLabel: string;
  close: number | null;
  buyVolume: number;
  sellVolume: number;
};

function mergeChartData(daily: DailyActivityPoint[], prices: IndexPricePoint[]): ChartDataPoint[] {
  const priceByDate = new Map(prices.map(p => [p.date, p.close]));
  const dailyByDate = new Map(
    daily.map(d => [d.date, { buyVolume: d.buy_volume_dollars, sellVolume: d.sell_volume_dollars }]),
  );
  const dates = new Set([...priceByDate.keys(), ...dailyByDate.keys()]);
  const sorted = [...dates].sort();
  return sorted.map(date => {
    const d = dailyByDate.get(date);
    const close = priceByDate.get(date) ?? null;
    return {
      date,
      dateLabel: formatDateLabel(date),
      close,
      buyVolume: d?.buyVolume ?? 0,
      sellVolume: d?.sellVolume ?? 0,
    };
  });
}

/** Matches `getIndexBarsLast30Days` in `../data.tsx` (daily closes for chart overlay). */
async function fetchIndexBarsLast30Days(chartManager: ChartManager, symbol: string): Promise<IndexPricePoint[]> {
  const to = DateTime.now().toFormat('yyyy-MM-dd');
  const from = DateTime.now().minus({ days: 30 }).toFormat('yyyy-MM-dd');
  const result = await safeTimeout(chartManager.getBars(symbol, { from, to, interval: '1d' }), 5000);
  const candles = result?.ok?.candles ?? [];
  return candles.map(c => ({
    date: (c.dateTime || c.time || '').toString().slice(0, 10),
    close: c.close ?? 0,
  }));
}

interface InsiderActivityVsIndexChartProps {
  dailyActivity: DailyActivityPoint[];
  initialIndexPrices: IndexPricePoint[];
  initialSymbol?: string;
}

export function InsiderActivityVsIndexChart({
  dailyActivity,
  initialIndexPrices,
  initialSymbol = 'SPY',
}: InsiderActivityVsIndexChartProps) {
  const [selectedSymbol, setSelectedSymbol] = useState(initialSymbol);
  const [indexPrices, setIndexPrices] = useState<IndexPricePoint[]>(() =>
    selectedSymbol === initialSymbol ? initialIndexPrices : [],
  );
  const [loadingPrices, setLoadingPrices] = useState(selectedSymbol !== initialSymbol);
  const session = useContext(SessionContext);
  const chartManager = session.getManager(ChartManager);
  const isMobile = isMobileDevice();

  const fetchPrices = useCallback(
    async (symbol: string) => {
      setLoadingPrices(true);
      try {
        const prices = await fetchIndexBarsLast30Days(chartManager, symbol);
        setIndexPrices(prices);
      } catch {
        setIndexPrices([]);
      } finally {
        setLoadingPrices(false);
      }
    },
    [chartManager],
  );

  React.useEffect(() => {
    if (selectedSymbol === initialSymbol) {
      setIndexPrices(initialIndexPrices);
      setLoadingPrices(false);
    } else {
      fetchPrices(selectedSymbol);
    }
  }, [selectedSymbol, initialSymbol, initialIndexPrices, fetchPrices]);

  const chartData = useMemo(() => mergeChartData(dailyActivity, indexPrices), [dailyActivity, indexPrices]);

  const priceAxisDomain = useMemo((): { min?: number; max?: number } => {
    const closes = indexPrices.map(p => p.close).filter((c): c is number => Number.isFinite(c));
    if (closes.length === 0) return {};
    const min = Math.min(...closes);
    const max = Math.max(...closes);
    const span = max - min;
    const margin = span > 0 ? span * 0.1 : Math.abs(min) * 0.1 || 1;
    return { min: min - margin, max: max + margin };
  }, [indexPrices]);

  const indexLabel = INDEX_OPTIONS.find(o => o.symbol === selectedSymbol)?.label ?? selectedSymbol;

  const chartOptions: EChartsOption = useMemo(
    () => ({
      grid: { top: 32, right: 64, bottom: 56, left: 64 },
      legend: {
        bottom: 0,
        data: [indexLabel, 'Buy Volume', 'Sell Volume'],
        textStyle: { fontSize: 12 },
      },
      tooltip: {
        axisPointer: { crossStyle: { color: '#999' }, type: 'cross' },
        trigger: 'axis',
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const title = (rows[0] as { axisValueLabel?: string; axisValue?: string })?.axisValueLabel
            ?? (rows[0] as { axisValue?: string })?.axisValue
            ?? '';
          const body = rows
            .map(r => {
              const row = r as { marker: string; seriesName: string; value: number | null };
              const formatted =
                row.seriesName === indexLabel
                  ? row.value != null
                    ? '$' + Number(row.value).toFixed(2)
                    : '—'
                  : formatTradeSize(Number(row.value));
              return `${row.marker}${row.seriesName}<span style="float: right; margin-left: 20px"><b>${formatted}</b></span>`;
            })
            .join('<br />');
          return `${title}<br />${body}`;
        },
      },
      xAxis: [
        {
          axisLabel: { fontSize: 11 },
          axisLine: { lineStyle: { color: '#5b7292' } },
          axisTick: { show: false },
          data: chartData.map(d => d.dateLabel),
          type: 'category',
        },
      ],
      yAxis: [
        {
          axisLabel: {
            fontSize: 11,
            formatter: (v: number) => (typeof v === 'number' ? v.toFixed(0) : String(v)),
          },
          axisLine: { lineStyle: { color: '#3f83f8' }, show: true },
          axisTick: { show: false },
          max: priceAxisDomain.max,
          min: priceAxisDomain.min,
          name: `${indexLabel} Price`,
          nameGap: 48,
          nameLocation: 'middle',
          nameTextStyle: { fontSize: 11 },
          position: 'left',
          scale: true,
          splitLine: { lineStyle: { color: '#e1ebfa', type: 'dashed' } },
          type: 'value',
        },
        {
          axisLabel: {
            fontSize: 11,
            formatter: (v: number) => formatShareAxisLabel(Number(v)),
          },
          axisLine: { lineStyle: { color: '#5b7292' }, show: true },
          axisTick: { show: false },
          name: 'Insider Buy/Sell Volume, ($)',
          nameGap: 56,
          nameLocation: 'middle',
          nameRotate: -90,
          nameTextStyle: { fontSize: 11 },
          position: 'right',
          splitLine: { show: false },
          type: 'value',
        },
      ],
      series: [
        {
          data: chartData.map(d => d.buyVolume),
          itemStyle: { color: '#30bf60' },
          name: 'Buy Volume',
          type: 'bar',
          yAxisIndex: 1,
        },
        {
          data: chartData.map(d => d.sellVolume),
          itemStyle: { color: '#ff4050' },
          name: 'Sell Volume',
          type: 'bar',
          yAxisIndex: 1,
        },
        {
          connectNulls: true,
          data: chartData.map(d => d.close),
          itemStyle: { color: '#3f83f8' },
          lineStyle: { color: '#3f83f8', width: 2 },
          name: indexLabel,
          showSymbol: true,
          smooth: false,
          symbolSize: 6,
          type: 'line',
          yAxisIndex: 0,
        },
      ],
    }),
    [chartData, indexLabel, priceAxisDomain.max, priceAxisDomain.min],
  );

  return (
    <div className={styles['insider-activity-chart']}>
      <h3 className={styles['section-title']}>Insider Trading Activity Vs Sector Or Index Performance</h3>
      <div className={styles['chart-index-pills']}>
        {INDEX_OPTIONS.map(opt => (
          <button
            key={opt.symbol}
            type="button"
            className={selectedSymbol === opt.symbol ? styles['chart-pill-active'] : styles['chart-pill']}
            onClick={() => setSelectedSymbol(opt.symbol)}
            aria-pressed={selectedSymbol === opt.symbol}
          >
            {isMobile ? opt.symbol : opt.label}
          </button>
        ))}
      </div>
      <div className={styles['chart-container']} style={{ minHeight: 320 }}>
        {loadingPrices && chartData.length === 0 ? (
          <div className={styles['top-companies-empty']}>Loading chart…</div>
        ) : (
          <ReactECharts
            notMerge={true}
            option={chartOptions}
            style={{ height: '320px', width: '100%' }}
          />
        )}
      </div>
    </div>
  );
}
